"use client";

import { forwardRef, type SelectHTMLAttributes } from "react";
import styles from "./Form.module.css";
import { useFieldContext } from "./Field";

/**
 * Select — natywny `<select>` z brand-styled chevron + floating label support.
 *
 * **Floating label dla select:** select nigdy nie jest "empty" w sensie
 * HTML (zawsze ma value). Żeby floating label działał:
 *   1. `<Select placeholder="Wybierz…">` (opcjonalne) — wstrzykuje sentinel
 *      `<option value="" disabled hidden>` jako pierwszą opcję
 *   2. Atrybut `data-empty` na `<select>` mówi CSS-owi czy value jest sentinelem
 *      ("" lub 0) — wtedy label "w środku" jak placeholder
 *   3. Gdy value != sentinel -> `data-empty="false"` -> label "u góry"
 *
 * **Bez `placeholder` prop:** select renderuje normalnie, label ZAWSZE "u góry"
 * (zawsze valid value, nie ma stanu pustego dla floatingu).
 *
 * **Auto-binding z `<Field>`:** id + aria-*. Select nie potrzebuje placeholder=" "
 * (to atrybut tylko dla input/textarea).
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, success, placeholder, className, children, ...rest },
  ref,
) {
  const ctx = useFieldContext();
  const effectiveInvalid = invalid ?? ctx?.invalid ?? false;
  const stateClass = effectiveInvalid
    ? styles.invalid
    : success
      ? styles.success
      : "";

  /* `data-empty` = true gdy value jest pusty string lub 0 (sentinel option).
     CSS w Form.module.css patrzy na ten atrybut żeby zdecydować pozycję
     label-a. Bez placeholder propu select jest "zawsze wypełniony" — dla
     spójności CSS, ustawiamy data-empty="false" zawsze (label u góry). */
  const value = rest.value ?? rest.defaultValue;
  const isEmpty =
    placeholder != null && (value === "" || value === 0 || value === "0");

  return (
    <select
      ref={ref}
      {...rest}
      id={rest.id ?? ctx?.id}
      aria-describedby={rest["aria-describedby"] ?? ctx?.describedBy}
      aria-invalid={rest["aria-invalid"] ?? (effectiveInvalid || undefined)}
      aria-required={rest["aria-required"] ?? (ctx?.required || undefined)}
      data-empty={isEmpty ? "true" : "false"}
      className={[styles.control, styles.select, stateClass, className]
        .filter(Boolean)
        .join(" ")}
    >
      {placeholder != null && (
        <option value="" disabled hidden>
          {placeholder}
        </option>
      )}
      {children}
    </select>
  );
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  invalid?: boolean;
  success?: boolean;
  /**
   * Placeholder text — gdy ustawiony, Select renderuje sentinel option na
   * początku (`<option value="" disabled hidden>{placeholder}</option>`).
   * Aktywuje floating label gdy value="" lub value=0 (animowane przejście
   * w label "u góry" po wyborze opcji). Bez tego prop'a label zawsze
   * "u góry" (select zawsze ma value).
   */
  placeholder?: string;
};
